import { EditCalendarOutlined } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Fragment, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { GlobalDataContext } from "../../../../../contexts/GlobalData";
import { EDIT_APPOINTMENT } from "../../../../../enums/PermissionTitles";
import useUser from "../../../../../hooks/useUser";
import EditAppointment from "../../../components/editing/EditAppointment";
import useAppointmentGeneral from "../../hooks/useAppointmentGeneral";
import { canManageAppointment } from "../../utils/appointmentActionRules";
import HeaderIconButton from "./HeaderIconButton";

export default function EditAppointmentButton({ appointment }) {
  const { t } = useTranslation();
  const { data } = useAppointmentGeneral();
  const { roles } = useUser();
  const { isPermissionAllowed } = useContext(GlobalDataContext);
  const [open, setOpen] = useState(false);

  if (roles.master || !canManageAppointment(appointment, roles)) {
    return null;
  }

  const hasPermission = isPermissionAllowed(EDIT_APPOINTMENT);

  return (
    <Fragment>
      <Tooltip
        title={
          hasPermission ? t("EditAppointmnet") : t("NoPermissionForThisAction")
        }
      >
        <span>
          <HeaderIconButton
            disabled={!hasPermission || appointment?.pet?.deceased}
            onClick={() => setOpen(true)}
            testId={"appointment.details.edit-button"}
          >
            <EditCalendarOutlined />
          </HeaderIconButton>
        </span>
      </Tooltip>
      <EditAppointmentDialog
        open={open}
        onClose={() => setOpen(false)}
        appointment={appointment}
        usersPets={data?.usersPets}
      />
    </Fragment>
  );
}

function EditAppointmentDialog({ open, onClose, appointment, usersPets }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      data-testid={"appointment.edit-dialog"}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ p: 2 }}
      >
        <Typography variant="h6" component="h2" fontWeight={600}>
          {t("EditAppointmnet")}
        </Typography>
        <IconButton
          onClick={onClose}
          title={t("Close")}
          data-testid={"appointment.edit-dialog.close-button"}
        >
          <CloseIcon />
        </IconButton>
      </Stack>
      <Divider sx={{ opacity: 0.6 }} />
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <EditAppointment
          handleEditing={onClose}
          allPets={usersPets}
          appointmentProp={appointment}
        />
      </DialogContent>
    </Dialog>
  );
}
